<script setup lang="ts">
import type { ErrorDto } from '@optionway/models';
import { z } from 'zod';

const prefix = 'components.home.newsletterSignup';
const { $appConfig } = useNuxtApp();
const gtmStore = useGtmStore();

const email = ref('');
const isSubscribed = useState('newsletter-subscribed', () => false);
const hasSubmitted = ref(false);
const mutationError = ref<'INVALID_EMAIL' | 'SERVER_ERROR' | 'GENERIC' | undefined>();

const emailSchema = z.string().email();
const isEmailEmpty = computed(() => hasSubmitted.value && !email.value);
const isEmailInvalid = computed(
  () => hasSubmitted.value && !!email.value && !emailSchema.safeParse(email.value).success,
);

const { mutate: subscribeNewsletter, asyncStatus } = useMutation<undefined, string, ErrorDto>({
  key: ['home', 'newsletter'],
  mutation: (emailValue) =>
    $fetch<undefined>(`${$appConfig.api?.url}/home/newsletter`, {
      method: 'PUT',
      body: { email: emailValue },
    }),
  onSuccess: () => {
    email.value = '';
    isSubscribed.value = true;
    gtmStore.trackNewsletterSignup();
  },
  onError: (error) => {
    if (error.statusCode === 422) mutationError.value = 'INVALID_EMAIL';
    else if ((error.statusCode ?? 0) >= 500) mutationError.value = 'SERVER_ERROR';
    else mutationError.value = 'GENERIC';
  },
});

const hasInputInvalid = computed(
  () => isEmailEmpty.value || isEmailInvalid.value || mutationError.value === 'INVALID_EMAIL',
);

const newsletterSignUp = () => {
  if (asyncStatus.value === 'loading') return;
  hasSubmitted.value = true;
  mutationError.value = undefined;
  if (!email.value || isEmailInvalid.value) return;
  subscribeNewsletter(email.value);
};
</script>

<template>
  <div class="bg-brand-primary-700 px-4 py-6 md:px-6">
    <UiCustomContainer>
      <div class="flex flex-col gap-6 md:flex-row md:items-center md:justify-between md:gap-12">
        <div class="flex flex-col gap-1 md:max-w-100">
          <h2 class="text-pretty text-2xl font-semibold text-text-inverse">
            {{ $t(`${prefix}.title`) }}
          </h2>
          <p class="text-pretty text-sm font-normal text-text-inverse">
            {{ $t(`${prefix}.description`) }}
          </p>
        </div>

        <div class="flex w-full flex-col gap-2 md:max-w-lg">
          <Transition
            enter-active-class="transition-all duration-300"
            enter-from-class="opacity-0 translate-y-2"
            enter-to-class="opacity-100 translate-y-0"
            leave-active-class="transition-all duration-300"
            leave-from-class="opacity-100 translate-y-0"
            leave-to-class="opacity-0 translate-y-2"
            mode="out-in"
          >
            <div
              v-if="isSubscribed"
              role="status"
              class="flex items-center gap-3 text-pretty text-sm font-normal text-text-inverse"
            >
              <UiCustomAnimatedCheckIcon
                aria-hidden="true"
                class="size-8 shrink-0 text-white"
              />
              {{ $t(`${prefix}.subscribed`) }}
            </div>

            <div
              v-else
              class="flex flex-col gap-2"
            >
              <UiLabel
                for="newsletterSignupInput"
                class="sr-only"
              >
                {{ $t(`${prefix}.placeholder`) }}
              </UiLabel>
              <div class="flex flex-col gap-2 sm:flex-row sm:items-start">
                <div class="flex w-full flex-col gap-1">
                  <UiInput
                    id="newsletterSignupInput"
                    v-model="email"
                    class="h-10 bg-white font-normal placeholder:text-gray-500 focus-visible:ring-white/50"
                    type="email"
                    :placeholder="$t(`${prefix}.placeholder`)"
                    :aria-invalid="hasInputInvalid ? true : undefined"
                    :aria-describedby="hasInputInvalid || mutationError ? 'newsletter-error' : undefined"
                    @keydown.enter="newsletterSignUp"
                  />
                  <UiFieldError
                    v-if="isEmailEmpty"
                    id="newsletter-error"
                    class="text-xs text-red-300"
                  >
                    {{ $t(`${prefix}.emailRequired`) }}
                  </UiFieldError>
                  <UiFieldError
                    v-else-if="isEmailInvalid || mutationError === 'INVALID_EMAIL'"
                    id="newsletter-error"
                    class="text-xs text-red-300"
                  >
                    {{ $t(`${prefix}.invalidEmail`) }}
                  </UiFieldError>
                  <UiFieldError
                    v-else-if="mutationError === 'GENERIC'"
                    id="newsletter-error"
                    class="text-xs text-red-300"
                  >
                    {{ $t(`${prefix}.error`) }}
                  </UiFieldError>
                  <UiFieldError
                    v-else-if="mutationError === 'SERVER_ERROR'"
                    id="newsletter-error"
                    class="text-xs text-red-300"
                  >
                    {{ $t(`${prefix}.serverError`) }}
                  </UiFieldError>
                  <p class="hidden text-pretty text-xs font-normal text-text-inverse sm:block">
                    {{ $t(`${prefix}.note`) }}
                  </p>
                </div>
                <UiButton
                  variant="primary"
                  color="white"
                  class="h-10 shrink-0 sm:w-auto sm:px-5"
                  type="button"
                  :loading="asyncStatus === 'loading'"
                  :loading-label="$t('components.common.loading')"
                  @click="newsletterSignUp"
                >
                  {{ $t(`${prefix}.cta`) }}
                </UiButton>
                <p class="text-pretty text-xs font-normal text-text-inverse sm:hidden">
                  {{ $t(`${prefix}.note`) }}
                </p>
              </div>
            </div>
          </Transition>
        </div>
      </div>
    </UiCustomContainer>
  </div>
</template>
